class CollectionProductSearch {
  static PARAM = 'collection_q';

  constructor() {
    this.term = this.#getStoredTerm();
    this.matchingHandles = null;
    this.#searchId = 0;
    this.#expanded = false;
    this.#expanding = null;
    this.#bindInputs();
    this.#syncInputsFromTerm();

    document.addEventListener('krown:main-collection-grid:filtered', (event) => {
      this.#expanded = false;
      this.#expanding = null;
      if (event.detail?.fromCollectionSearch) return;
      this.#syncInputsFromTerm();
      if (this.term) this.#runSearch(this.term);
      else this.#showAllProducts();
    });

    document.addEventListener('click', (event) => {
      const pill = event.target.closest('[data-js-collection-search-remove]');
      if (pill) {
        event.preventDefault();
        event.stopPropagation();
        this.#clearSearch();
        return;
      }

      const clearAll = event.target.closest('[data-js-clear-all-filters]');
      if (clearAll) {
        this.#clearSearch();
      }
    });

    if (this.term) {
      this.#runSearch(this.term);
    }
  }

  #getStoredTerm() {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get(CollectionProductSearch.PARAM)?.trim();
    if (fromUrl) {
      sessionStorage.setItem(CollectionProductSearch.PARAM, fromUrl);
      return fromUrl;
    }

    return sessionStorage.getItem(CollectionProductSearch.PARAM)?.trim() || '';
  }

  #storeTerm(term) {
    if (term) {
      sessionStorage.setItem(CollectionProductSearch.PARAM, term);
    } else {
      sessionStorage.removeItem(CollectionProductSearch.PARAM);
    }
  }

  #bindInputs() {
    document.addEventListener('input', (event) => {
      if (!event.target.matches('[data-js-collection-search]')) return;
      this.#onInput(event.target);
    });

    document.addEventListener('search', (event) => {
      if (!event.target.matches('[data-js-collection-search]')) return;
      this.#onInput(event.target);
    });
  }

  #syncInputsFromTerm() {
    document.querySelectorAll('[data-js-collection-search]').forEach((input) => {
      input.value = this.term;
    });
  }

  #syncInputs(sourceInput) {
    document.querySelectorAll('[data-js-collection-search]').forEach((input) => {
      if (input !== sourceInput) input.value = sourceInput.value;
    });
  }

  #debounceTimer = null;
  #searchId = 0;
  #expanded = false;
  #expanding = null;

  #onInput(sourceInput) {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#runSearch(sourceInput.value.trim(), sourceInput);
    }, 300);
  }

  async #runSearch(term, sourceInput = null) {
    const searchId = ++this.#searchId;
    this.term = term;
    if (sourceInput) this.#syncInputs(sourceInput);
    else this.#syncInputsFromTerm();
    this.#storeTerm(term);
    this.#updateUrl();

    if (!this.term) {
      this.matchingHandles = null;
      this.#showAllProducts();
      return;
    }

    await this.#ensureExpandedCatalog(searchId);
    if (searchId !== this.#searchId) return;

    const handles = await this.#fetchMatchingHandles(this.term);
    if (searchId !== this.#searchId) return;

    this.matchingHandles = handles;
    this.#applyFilter();
  }

  #clearSearch() {
    clearTimeout(this.#debounceTimer);
    this.#searchId += 1;
    this.term = '';
    this.matchingHandles = null;
    this.#storeTerm('');
    this.#syncInputsFromTerm();
    this.#updateUrl();
    this.#showAllProducts();
  }

  async #ensureExpandedCatalog(searchId) {
    if (this.#expanded) return;
    if (this.#expanding) {
      await this.#expanding;
      return;
    }

    const section = document.getElementById('main-collection-product-grid');
    if (!section) return;

    const pages = parseInt(section.dataset.paginatePages || '1', 10);
    const sectionId = section.dataset.id;
    if (!sectionId || pages <= 1) {
      this.#expanded = true;
      return;
    }

    this.#expanding = (async () => {
      section.classList.add('loading');

      try {
        const existing = new Set(
          [...section.querySelectorAll('[data-js-product-item]')].map((item) => item.dataset.productHandle)
        );
        const footer = section.querySelector('[data-js-collection-footer]');
        const pageNumbers = [];

        for (let page = 1; page <= pages; page += 1) {
          pageNumbers.push(page);
        }

        const results = await Promise.all(
          pageNumbers.map((page) => this.#fetchPageProducts(page, sectionId))
        );

        if (searchId !== this.#searchId) return;

        results.flat().forEach((node) => {
          const handle = node.dataset.productHandle;
          if (!handle || existing.has(handle)) return;
          existing.add(handle);
          node.setAttribute('data-js-collection-search-extra', '');
          if (footer) section.insertBefore(node, footer);
          else section.appendChild(node);
        });

        this.#expanded = true;
      } finally {
        section.classList.remove('loading');
      }
    })();

    try {
      await this.#expanding;
    } finally {
      this.#expanding = null;
    }
  }

  async #fetchPageProducts(page, sectionId) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString());
    if (!response.ok) return [];

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return [...doc.querySelectorAll('#main-collection-product-grid [data-js-product-item]')].map(
      (item) => item.cloneNode(true)
    );
  }

  #collapseExpandedCatalog() {
    document.querySelectorAll('[data-js-collection-search-extra]').forEach((item) => item.remove());
    this.#expanded = false;
  }

  #showAllProducts() {
    this.#collapseExpandedCatalog();

    const grid = document.getElementById('CollectionProductGrid');
    if (!grid) return;

    const items = grid.querySelectorAll('[data-js-product-item]');
    items.forEach((item) => {
      item.hidden = false;
      item.classList.remove('hidden');
    });

    const section = document.getElementById('main-collection-product-grid');
    const total = parseInt(section?.dataset.paginateItems || String(items.length), 10) || items.length;

    this.#updateCount(items.length, total);
    this.#toggleEmpty(grid, items.length, '');
    this.#toggleFooter(false);
    this.#updateActivePills();
  }

  #updateUrl() {
    const params = new URLSearchParams(window.location.search);

    if (this.term) {
      params.set(CollectionProductSearch.PARAM, this.term);
    } else {
      params.delete(CollectionProductSearch.PARAM);
    }

    const search = params.toString();
    history.replaceState(history.state, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
  }

  async #fetchMatchingHandles(term) {
    const collectionPath = window.location.pathname;
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product',
      'resources[limit]': '50',
      'resources[options[unavailable_products]]': 'last',
      'resources[options[prefix]]': 'last'
    });

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}search/suggest.json?${params.toString()}`);
      if (!response.ok) return this.#matchHandlesLocally(term);

      const data = await response.json();
      const products = data?.resources?.results?.products || [];
      const handles = new Set(
        products
          .filter((product) => product.url?.includes(collectionPath) || collectionPath.endsWith('/all'))
          .map((product) => product.handle)
      );

      if (handles.size > 0) return handles;
    } catch (error) {
      // Fall back to local title matching when suggest is unavailable.
    }

    return this.#matchHandlesLocally(term);
  }

  #matchHandlesLocally(term) {
    const handles = new Set();
    const normalizedTerm = term.toLowerCase();

    document.querySelectorAll('#CollectionProductGrid [data-js-product-item]').forEach((item) => {
      const title = item.dataset.productTitle?.toLowerCase() || '';
      if (title.includes(normalizedTerm) && item.dataset.productHandle) {
        handles.add(item.dataset.productHandle);
      }
    });

    return handles;
  }

  #applyFilter() {
    const grid = document.getElementById('CollectionProductGrid');
    if (!grid) return;

    if (!this.term) {
      this.#showAllProducts();
      return;
    }

    const items = grid.querySelectorAll('[data-js-product-item]');
    const term = this.term.toLowerCase();
    let visible = 0;

    items.forEach((item) => {
      const title = item.dataset.productTitle?.toLowerCase() || '';
      const handle = item.dataset.productHandle || '';
      const handleMatch = this.matchingHandles ? this.matchingHandles.has(handle) : false;
      const titleMatch = title.includes(term);
      const match = handleMatch || titleMatch;

      item.hidden = !match;
      item.classList.toggle('hidden', !match);
      if (match) visible += 1;
    });

    this.#updateCount(visible, items.length);
    this.#toggleEmpty(grid, visible, term);
    this.#toggleFooter(true);
    this.#updateActivePills();
  }

  #updateActivePills() {
    document.querySelectorAll('.active-facets-desktop, .active-facets-mobile').forEach((container) => {
      container.querySelectorAll('[data-js-collection-search-pill]').forEach((pill) => pill.remove());

      if (!this.term) return;

      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'active-facets__button active-facets__button--light button button--small button--outline';
      pill.setAttribute('data-js-collection-search-pill', '');
      pill.setAttribute('data-js-collection-search-remove', '');

      const labelTemplate = window.KROWN?.settings?.collection_search_active_label;
      const label = labelTemplate
        ? labelTemplate.replace('[[terms]]', this.term)
        : `Search: ${this.term}`;

      pill.append(document.createTextNode(label));
      const closeIcon = window.KROWN?.settings?.symbols?.close_small;
      if (closeIcon) {
        pill.insertAdjacentHTML('beforeend', closeIcon);
      }

      container.prepend(pill);
    });
  }

  #updateCount(visible, total) {
    const countLabel = window.KROWN?.settings?.collection_search_count;
    const simpleLabel = window.KROWN?.settings?.collection_search_count_simple;

    document.querySelectorAll('#CollectionProductCount').forEach((el) => {
      if (!this.term) {
        el.textContent = simpleLabel
          ? simpleLabel.replace('[[count]]', String(total))
          : `${total} products`;
        return;
      }

      if (countLabel) {
        el.textContent = countLabel
          .replace('[[visible]]', String(visible))
          .replace('[[total]]', String(total));
      } else {
        el.textContent = `${visible} of ${total} products`;
      }
    });
  }

  #toggleEmpty(grid, visible, term) {
    let empty = grid.querySelector('[data-js-collection-search-empty]');

    if (term && visible === 0) {
      if (!empty) {
        empty = document.createElement('p');
        empty.className = 'no-content-message collection-search-empty';
        empty.setAttribute('data-js-collection-search-empty', '');
        grid.appendChild(empty);
      }

      const template = window.KROWN?.settings?.collection_search_empty;
      empty.textContent = template
        ? template.replace('[[terms]]', term)
        : `No products found for "${term}"`;
      empty.hidden = false;
    } else if (empty) {
      empty.hidden = true;
    }
  }

  #toggleFooter(hide) {
    document.querySelectorAll('[data-js-collection-footer]').forEach((footer) => {
      footer.hidden = hide;
    });
  }

  static mergeIntoSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams);
    const term = sessionStorage.getItem(CollectionProductSearch.PARAM);

    if (term) params.set(CollectionProductSearch.PARAM, term);
    else params.delete(CollectionProductSearch.PARAM);

    return params.toString();
  }
}

window.CollectionProductSearch = CollectionProductSearch;

if (document.querySelector('[data-js-collection-search]')) {
  window.collectionProductSearch = new CollectionProductSearch();
}
